import React from 'react';
import formStyle from 'acscp/components/flightRequest/form/flightRequestForm.module.scss';
import style from 'acscp/components/flightRequest/form/flightRequestLegsForm.module.scss';
import classNames from 'classnames';
import { FlightRequestFormLeg } from 'acscp/components/flightRequest/form/helpers/flightRequestFormMapper';
import { FlightRequestLegForm } from 'acscp/components/flightRequest/form/flightRequestLegForm';
import { FormattedMessage } from 'react-intl';
import { defaultLeg } from './helpers/defaultLeg';
import { MAXIMUM_MULTILEG_LEGS, MINIMUM_MULTILEG_LEGS } from './helpers/flightRequestConstants';
import { Tooltip } from 'acscp/components/common/tooltip';
import { QuestionMarkCircle } from 'acscp/components/common/questionMarkCircle';
import { PlusIcon } from 'acscp/components/common/icons/plusIcon';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { FlightRequestLegHeading } from 'acscp/components/flightRequest/form/flightRequestLegHeading';

const TOOLTIP_OFFSET_TOP = -10;
const TOOLTIP_OFFSET_LEFT = -10;

interface FlightRequestLegsFormProps {
    legs: FlightRequestFormLeg[];
    onChange: (legs: FlightRequestFormLeg[]) => void;
    showAddAndRemoveButtons: boolean;
    enquiryButtons: React.ReactNode[];
    registerValidator: (validateLegs: () => boolean) => void;
}

class FlightRequestLegFormWithIntl extends React.Component<FlightRequestLegsFormProps & InjectedIntlProps, {}> {
    private legStateValidators: {[key: string]: () => boolean} = {};

    componentDidMount(): void {
        this.props.registerValidator(this.validateLegs);
    }

    componentWillReceiveProps(nextProps: FlightRequestLegsFormProps & InjectedIntlProps): void {
        // Remove the legStateValidators for legs which are removed from parent components
        if (nextProps.legs.length < this.props.legs.length) {
            const newLegIds = nextProps.legs.map(leg => leg.id);
            const deletedLegIds = this.props.legs.map(leg => leg.id).filter(id => newLegIds.indexOf(id) === -1);
            deletedLegIds.forEach(id => delete this.legStateValidators[id]);
        }
    }

    validateLegs = () => {
        for (const key of Object.keys(this.legStateValidators)) {
            if (!this.legStateValidators[key]()) {
                return false;
            }
        }
        return true;
    }

    getLegChangeHandlerForIndex = (index: number) => (leg: FlightRequestFormLeg) => {
        const legs = [...this.props.legs];
        legs[index] = leg;
        this.props.onChange(legs);
    }

    canAddLegs = () => {
        return this.props.legs.length < MAXIMUM_MULTILEG_LEGS;
    }

    addLeg = () => {
        const legs = this.props.legs.map(l => ({...l, isExpanded: false}));
        const latestLeg = legs[legs.length - 1];
        const legToAdd = defaultLeg();
        legToAdd.departureAirport = latestLeg.arrivalAirport;
        legToAdd.departureLocation = latestLeg.arrivalLocation;
        legToAdd.date = latestLeg.date;
        legToAdd.passengers = latestLeg.passengers;
        legs.push(legToAdd);
        this.props.onChange(legs);
    }

    canRemoveLegs = () => {
        return this.props.legs.length > MINIMUM_MULTILEG_LEGS;
    }

    removeLeg = (index: number) => {
        if (!this.canRemoveLegs()) {
            return;
        }
        const legs = [...this.props.legs];

        delete this.legStateValidators[legs[index].id];
        legs.splice(index, 1);
        this.props.onChange(legs);
    }

    setIsExpanded = (index: number, isExpanded: boolean) => {
        const legs = [...this.props.legs];
        legs[index] = {...legs[index], isExpanded};
        this.props.onChange(legs);
    }

    setShowValidationErrors = (index: number, showValidationErrors: boolean) => {
        const legs = [...this.props.legs];
        legs[index] = {...legs[index], showValidationErrors};
        this.props.onChange(legs);
    }

    getLegHeadingClickHandlerForIndex = (index: number): void => {
        if (this.props.legs[index].isExpanded) {
            const legs = [...this.props.legs];
            legs[index] = {...legs[index], isExpanded: false, showValidationErrors: true};
            this.props.onChange(legs);
        } else {
            this.setIsExpanded(index, true);
        }
    }

    doneButtonClickHandler = (index: number) => {
        const legIsValid = this.legStateValidators[this.props.legs[index].id]();
        if (legIsValid) {
            this.setIsExpanded(index, false);
        } else {
            this.setShowValidationErrors(index, true);
        }
    }

    render(): JSX.Element {
        const {legs} = this.props;
        const legsCanCollapse = legs.length > 1;
        const allLegsAreCollapsed = legsCanCollapse && legs.every(l => !l.isExpanded);
        return (
            <div>
                <div className={classNames(formStyle.Row, formStyle.Header)}>
                    <div className={classNames(formStyle.Cell, formStyle.DepartureCell)}>
                        <FormattedMessage
                            id={'components.flightRequest.form.flightRequestLegForm.depart'}
                            defaultMessage={'DEPART'}
                        />
                    </div>
                    <div className={classNames(formStyle.Cell, formStyle.ArrivalCell)}>
                        <FormattedMessage
                            id={'components.flightRequest.form.flightRequestLegForm.arrive'}
                            defaultMessage={'ARRIVE'}
                        />
                    </div>
                    <div className={classNames(formStyle.Cell, formStyle.DateTypeCell)}/>
                    <div className={classNames(formStyle.Cell, formStyle.DateCell)}>
                        <FormattedMessage
                            id={'components.flightRequest.form.flightRequestLegForm.date'}
                            defaultMessage={'DATE'}
                        />
                    </div>
                    <div className={classNames(formStyle.Cell, formStyle.TimeCell)}>
                        <FormattedMessage
                            id={'components.flightRequest.form.flightRequestLegForm.time'}
                            defaultMessage={'TIME'}
                        />
                    </div>
                    <div className={classNames(formStyle.Cell, formStyle.PassengerCell)}>
                        <FormattedMessage
                            id={'components.flightRequest.form.flightRequestLegForm.passengers'}
                            defaultMessage={'PASSENGERS'}
                        />
                    </div>
                    <div
                        className={classNames(formStyle.Cell, formStyle.NotesCell, {
                            [formStyle.NoRemoveCell]: !this.props.showAddAndRemoveButtons
                        })}
                    >
                        <FormattedMessage
                            id={'components.flightRequest.form.flightRequestLegForm.notes'}
                            defaultMessage={'NOTES'}
                        />
                        <Tooltip
                            tooltipId={'components.flightRequest.form.flightRequestLegForm.notes'}
                            tooltipContents={
                                <FormattedMessage
                                    id={'components.flightRequest.form.flightRequestLegForm.notesTooltip'}
                                    defaultMessage={'Please add any additional requests, for example catering' +
                                        ' requirements, time slots and date or airport flexibility.'}
                                />
                            }
                            offsetTop={TOOLTIP_OFFSET_TOP}
                            offsetLeft={TOOLTIP_OFFSET_LEFT}
                            className={style.Tooltip}
                        >
                            <QuestionMarkCircle />
                        </Tooltip>
                    </div>
                    {this.props.showAddAndRemoveButtons &&
                        <div className={classNames(formStyle.Cell, formStyle.RemoveCell)}/>
                    }
                </div>
                {legs.map((leg: FlightRequestFormLeg, index) => (
                    <div key={index} className={classNames(style.LegWrapper, {[style.FirstLeg]: index === 0})}>
                        {legsCanCollapse &&
                        <FlightRequestLegHeading
                            leg={leg}
                            legNumber={index + 1}
                            isFirstLeg={index === 0}
                            isLastLeg={index === legs.length - 1}
                            showJourneyLine={allLegsAreCollapsed}
                            showLegError={leg.showValidationErrors && !this.legStateValidators[leg.id]()}
                            onClick={() => this.getLegHeadingClickHandlerForIndex(index)}
                        />}
                        <div className={classNames({[style.Collapsed]: legsCanCollapse && !leg.isExpanded})}>
                            <FlightRequestLegForm
                                leg={leg}
                                onChange={this.getLegChangeHandlerForIndex(index)}
                                showRemoveButton={this.props.showAddAndRemoveButtons}
                                canRemove={this.canRemoveLegs()}
                                onRemove={() => this.removeLeg(index)}
                                showDoneButton={legsCanCollapse}
                                onDone={() => this.doneButtonClickHandler(index)}
                                registerValidationCallback={(validateLeg: () => boolean) => this.legStateValidators[leg.id] = validateLeg}
                                previousLegDate={index === 0 ? '' : legs[index - 1].date}
                                previousLegTime={index === 0 ? '' : legs[index - 1].time}
                            />
                        </div>
                    </div>
                ))}
                <div className={formStyle.Buttons}>
                    {this.props.showAddAndRemoveButtons && this.canAddLegs() &&
                    <div className={formStyle.AddLegButtonContainer}>
                        <div className={formStyle.ButtonWrapper}>
                            <button type="button" className={classNames(formStyle.Button, style.AddButton)} onClick={this.addLeg}>
                                <PlusIcon/>
                                <FormattedMessage
                                    id={'components.flightRequest.form.flightRequestLegsForm.addAnotherLeg'}
                                    defaultMessage={'ADD ANOTHER LEG'}
                                />
                            </button>
                        </div>
                    </div>
                    }
                    <div className={formStyle.EnquiryButtonsContainer}>
                        {this.props.enquiryButtons}
                    </div>
                </div>
            </div>
        );
    }
}

const FlightRequestLegsForm = injectIntl(FlightRequestLegFormWithIntl);

export {FlightRequestLegsForm};
